import * as Yup from 'yup';
import { DataSeriesField } from '../../types/solutionAssetHierarchy';

export const widgetTitleValidation = Yup.string()
  .required('Widget title is required')
  .min(1, 'Title must be at least 1 character')
  .max(100, 'Title must not exceed 100 characters');

export const kustoQueryValidation = Yup.string()
  .required('Kusto query is required')
  .min(10, 'Query must be at least 10 characters');

export const refreshRateValidation = Yup.number()
  .required('Refresh rate is required')
  .min(5, 'Refresh rate must be at least 5 seconds')
  .max(3600, 'Refresh rate must not exceed 3600 seconds');

export const chartAxisValidation = Yup.object().shape({
  columnName: Yup.string(),
  label: Yup.string(),
});

export const yAxisValidation = Yup.object().shape({
  columnName: Yup.string(),
  label: Yup.string(),
});

export const dataSeriesValidation = Yup.array().of(
  Yup.object().shape({
    columnName: Yup.string().required('Column name is required'),
    label: Yup.string(),
  })
);
export const layoutValidation = Yup.object().shape({
  width: Yup.number().min(1).max(12).required(),
  height: Yup.number().min(1).max(8).required(),
  order: Yup.number().min(1).required(),
});

export const widgetConfigValidation = Yup.object().shape({
  title: widgetTitleValidation,
  chartType: Yup.string().required('Chart type is required'),
  dataSource: Yup.object().shape({
    type: Yup.string().required('Data source type is required'),
    method: Yup.string().required('HTTP method is required'),
    endpoint: Yup.string().required('API endpoint is required'),
    body: kustoQueryValidation,
    refreshRate: refreshRateValidation,
  }),
  chartConfiguration: Yup.object().shape({
    xAxis: chartAxisValidation,
    yAxis: yAxisValidation,
    dataSeries: dataSeriesValidation,
    showLegend: Yup.boolean(),
  }),
  layout: layoutValidation,
});

export const getXAxisLabel = (chartType: string): string => {
  if (chartType === 'pie') return 'Category Column';
  if (chartType === 'radar') return 'Category Column';
  if (chartType === 'metric') return 'Value Column';
  return 'X-Axis Column';
};

export const getXAxisTextLabel = (chartType: string): string => {
  if (chartType === 'pie') return 'Category Label';
  if (chartType === 'radar') return 'Category Label';
  if (chartType === 'metric') return 'Value Label';
  return 'X-Axis Label';
};

export const getXAxisPlaceholder = (chartType: string): string => {
  if (chartType === 'pie') return 'Enter category display label';
  if (chartType === 'radar') return 'Enter category display label';
  if (chartType === 'metric') return 'Enter value display label';
  return 'Enter X-axis display label';
};

export const getChartTypeNumber = (chartType: string): number => {
  const chartTypeMap: { [key: string]: number } = {
    line: 0,
    bar: 1,
    area: 2,
    pie: 3,
    scatter: 4,
    radar: 5,
    table: 6,
    metric: 7,
  };
  return chartTypeMap[chartType] || 0;
};

export const getDataSourceTypeNumber = (type: string): number => {
  const dataSourceTypeMap: { [key: string]: number } = {
    apiInternal: 0,
    apiExternal: 1,
  };
  return dataSourceTypeMap[type] || 0;
};

export const getDefaultWidgetConfig = (
  solutionId: string,
  existingWidgets: any[] = []
) => {
  const maxOrder =
    existingWidgets.length > 0
      ? Math.max(...existingWidgets.map((w) => w.layout?.order || 0))
      : 0;

  return {
    title: '',
    chartType: 'line',
    dataSource: {
      type: 'apiInternal',
      method: 'POST',
      endpoint: `/api/v1/data/solutions/${solutionId}/tenants/1/executerawkustoquery`,
      headers: [] as { key: string; value: string }[],
      body: '',
      refreshRate: 30,
    },
    chartConfiguration: {
      xAxis: { columnName: '', label: '' },
      yAxis: { columnName: '', label: '' },
      dataSeries: [] as DataSeriesField[],
      showLegend: true,
      selectedColumns: [] as DataSeriesField[],
    },
    layout: {
      width: 6,
      height: 4,
      order: maxOrder + 1,
    },
  };
};

export const prepareWidgetPayload = (config: any) => ({
  title: config.title || 'Untitled Widget',
  chartType: getChartTypeNumber(config.chartType || 'line'),
  dataSource: {
    type: getDataSourceTypeNumber(config.dataSource?.type || 'apiInternal'),
    method: config.dataSource?.method || 'POST',
    endpoint: config.dataSource?.endpoint || '',
    refreshRate: config.dataSource?.refreshRate || 30,
    body: config.dataSource?.body || '',
  },
  chartConfiguration: {
    showLegend: config.chartConfiguration?.showLegend ?? true,
    xAxis:
      config.chartType === 'table'
        ? null
        : {
            columnName: config.chartConfiguration?.xAxis?.columnName || '',
            label:
              config.chartConfiguration?.xAxis?.label ||
              config.chartConfiguration?.xAxis?.columnName ||
              '',
          },
    yAxis: (() => {
      if (
        config.chartType === 'table' ||
        config.chartType === 'radar' ||
        config.chartType === 'metric'
      ) {
        return null;
      }
      if (config.chartType === 'pie') {
        if (config.chartConfiguration?.yAxis?.columnName) {
          return {
            columnName: config.chartConfiguration?.yAxis?.columnName,
            label:
              config.chartConfiguration?.yAxis?.label ||
              config.chartConfiguration?.yAxis?.columnName ||
              '',
          };
        }
        return null;
      }
      return {
        columnName: config.chartConfiguration?.yAxis?.columnName || '',
        label:
          config.chartConfiguration?.yAxis?.label ||
          config.chartConfiguration?.yAxis?.columnName ||
          '',
      };
    })(),
    dataSeries: (() => {
      if (config.chartType === 'table') {
        return config.chartConfiguration?.selectedColumns || [];
      }
      if (config.chartType === 'pie') {
        return [];
      }
      if (config.chartType === 'metric') {
        return [];
      }
      return (config.chartConfiguration?.dataSeries || []).map((series) => ({
        ...series,
        label: series.label || series.columnName || '',
      }));
    })(),
  },
  layout: {
    width: config.layout?.width || 6,
    height: config.layout?.height || 4,
    order: config.layout?.order || 1,
  },
});

export const validateWidgetConfig = (
  config: any
): { isValid: boolean; error?: string } => {
  if (!config.dataSource?.body?.trim()) {
    return {
      isValid: false,
      error: 'Please enter a query before saving the widget',
    };
  }

  if (!config.title?.trim()) {
    return { isValid: false, error: 'Please enter a widget title' };
  }

  const { chartType, chartConfiguration } = config;

  if (chartType === 'pie') {
    if (!chartConfiguration?.xAxis?.columnName) {
      return {
        isValid: false,
        error: 'Pie charts require Category column (X-axis)',
      };
    }
  } else if (chartType === 'radar') {
    if (
      !chartConfiguration?.xAxis?.columnName ||
      !chartConfiguration?.dataSeries?.length
    ) {
      return {
        isValid: false,
        error:
          'Radar charts require Category column (X-axis) and at least one data series',
      };
    }
  } else if (chartType === 'table') {
    return { isValid: true };
  } else if (chartType === 'metric') {
    if (!chartConfiguration?.xAxis?.columnName) {
      return {
        isValid: false,
        error: 'Metric widgets require a value column',
      };
    }
    return { isValid: true };
  } else if (
    !chartConfiguration?.xAxis?.columnName ||
    !chartConfiguration?.dataSeries?.length
  ) {
    return {
      isValid: false,
      error: 'Charts require X-axis column and at least one data series',
    };
  }

  return { isValid: true };
};
